// src/services/api.js
import { useCallback, useState } from 'react';
import axios from 'axios';
import { useError } from '../contexts/ErrorContext';
import { Toast } from 'antd-mobile'
import { notification } from 'antd';

// Base URL for the API
const API_URL = process.env.REACT_APP_API_URL;

// Create an Axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Custom hook to use the error context
export const useApi = () => {
    const { setError, setContext } = useError();

    // Centralized error handling
    const handleError = useCallback((error, context) => {
        if (error.response && error.response.status === 401) {
            setContext(context || 'user');
            setError({ status: 401 });
        } else {
            const errorMsg = error.response?.data?.error;
            if (errorMsg && errorMsg !== "Failed to check email : user not found") {
                notification.error({
                    key: errorMsg,
                    message: 'API request error: ' + errorMsg,
                    placement: "top"
                });

                console.error('API request error:', error);
            }
        }
    }, [setError, setContext]);

    // GET request
    const fetchData = useCallback(async (endpoint, config = {}, context = 'user') => {
        try {
            const response = await api.get(endpoint, config);
            return response.data;
        } catch (error) {
            handleError(error, context);
            throw error;
        }
    }, [handleError]);

    // POST request
    const postData = useCallback(async (endpoint, data, config = {}, context = 'user') => {
        try {
            const response = await api.post(endpoint, data, config);
            return response.data;
        } catch (error) {
            handleError(error, context);
            throw error;
        }
    }, [handleError]);

    // PUT request
    const putData = useCallback(async (endpoint, data, config = {}, context = 'user') => {
        try {
            const response = await api.put(endpoint, data, config);
            return response.data;
        } catch (error) {
            handleError(error, context);
            // throw error;
        }
    }, [handleError]);

    // PATCH request
    const patchData = useCallback(async (endpoint, data, config = {}, context = 'user') => {
        try {
            const response = await api.patch(endpoint, data, config);
            return response.data;
        } catch (error) {
            console.error('API request error:', error.response || error);
            handleError(error, context);
            throw error;  // Add this line to propagate the error
        }
    }, [handleError]);

    // DELETE request
    const deleteData = useCallback(async (endpoint, config = {}, context = 'user') => {
        try {
            const response = await api.delete(endpoint, config);
            return response.data;
        } catch (error) {
            handleError(error, context);
            throw error;
        }
    }, [handleError]);

    return { fetchData, postData, putData, patchData, deleteData };
};
